import React, { useState } from 'react';
import { X, ShoppingBag, Star, User, ShoppingCart, Minus, Plus, Maximize2, ChevronLeft, ChevronRight, ZoomIn, CheckCircle } from 'lucide-react';
import { Product, Review, UserProfile, Order } from '../types';

interface ProductDetailProps {
  product: Product;
  onClose: () => void;
  onBuyNow: (product: Product, size: string, color: string, quantity: number) => void;
  onAddToCart: (product: Product, size: string, color: string, quantity: number) => void;
  reviews: Review[];
  onAddReview: (review: Review) => void;
  userProfile?: UserProfile | null;
  orders?: Order[];
}

export const ProductDetail: React.FC<ProductDetailProps> = ({ product, onClose, onBuyNow, onAddToCart, reviews, onAddReview, userProfile, orders = [] }) => {
  // Safe accessors
  const safeSizes = product.sizes || [];
  const safeColors = product.colors || [];
  const safeImages = product.images || [];

  const [selectedSize, setSelectedSize] = useState(safeSizes.length > 0 ? safeSizes[0] : 'N/A');
  const [selectedColor, setSelectedColor] = useState(safeColors.length > 0 ? safeColors[0] : 'N/A');
  const [mainImage, setMainImage] = useState(safeImages.length > 0 ? safeImages[0] : 'https://placehold.co/400x400?text=No+Image');
  const [quantity, setQuantity] = useState(1);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  
  // Review Form State
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [isReviewSubmitted, setIsReviewSubmitted] = useState(false);

  const productReviews = reviews.filter(r => r.productId === product.id);
  const averageRating = productReviews.length > 0 
    ? (productReviews.reduce((acc, curr) => acc + curr.rating, 0) / productReviews.length).toFixed(1)
    : null;

  // Check if user has purchased this item and if it is Delivered
  const hasPurchasedAndReceived = userProfile && orders.some(order => 
      // Check if user matches order info (using email or phone or logged in user logic)
      (order.customerEmail === userProfile.email || order.customerPhone === userProfile.phone || (userProfile.username && order.customerName === userProfile.username)) &&
      // Check if order is Delivered
      order.status === 'Delivered' &&
      // Check if product is in order
      order.items.some(item => item.id === product.id)
  );

  // WhatsApp Order Logic
  const handleWhatsAppOrder = () => {
    const phoneNumber = "923180508608";
    
    // Gather Customer Info with prompts if missing
    const name = userProfile?.username || prompt("Please enter your Name:", "") || "Guest";
    const phone = userProfile?.phone || prompt("Please enter your Phone Number:", "") || "";
    const address = prompt("Please enter your Delivery Address:", "") || "Not Provided";

    // Format Message
    const message = `*NEW WHATSAPP ORDER* 🛒
    
*PRODUCT DETAILS*
👟 *Name:* ${product.name}
📏 *Size:* ${selectedSize}
🎨 *Color:* ${selectedColor}
💰 *Price:* Rs. ${(product.discountPrice || product.price).toLocaleString()}
🔢 *Quantity:* ${quantity}

*CUSTOMER DETAILS*
👤 *Name:* ${name}
📞 *Phone:* ${phone}
🏠 *Address:* ${address}

Please confirm my order.`;

    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleSubmitReview = (e: React.FormEvent) => {
      e.preventDefault();
      
      const finalName = userProfile?.username || reviewerName;
      
      if (!finalName.trim() || !comment.trim()) return;

      const newReview: Review = {
          id: Date.now().toString(),
          productId: product.id,
          customerName: finalName,
          rating,
          comment,
          date: new Date().toISOString()
      };
      onAddReview(newReview);
      setIsReviewSubmitted(true);
  };

  const handleNextImage = (e?: React.MouseEvent) => {
      e?.stopPropagation();
      const currentIndex = safeImages.indexOf(mainImage);
      const nextIndex = (currentIndex + 1) % safeImages.length;
      setMainImage(safeImages[nextIndex]);
  };

  const handlePrevImage = (e?: React.MouseEvent) => {
      e?.stopPropagation();
      const currentIndex = safeImages.indexOf(mainImage);
      const prevIndex = (currentIndex - 1 + safeImages.length) % safeImages.length;
      setMainImage(safeImages[prevIndex]);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black bg-opacity-75 flex items-center justify-center p-4">
      {/* Lightbox / Zoom Modal */}
      {isZoomOpen && (
        <div className="fixed inset-0 z-[100] bg-black bg-opacity-95 flex flex-col items-center justify-center p-4 animate-fade-in" onClick={() => setIsZoomOpen(false)}>
            <button className="absolute top-4 right-4 text-white p-2 bg-gray-800 rounded-full hover:bg-gray-700 z-[110]" onClick={() => setIsZoomOpen(false)}>
                <X size={28} />
            </button>
            
            <div className="relative max-w-5xl w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                <img src={mainImage} className="max-h-[85vh] max-w-full object-contain rounded-md" />
                
                {safeImages.length > 1 && (
                    <>
                        <button onClick={handlePrevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 p-3 rounded-full text-white backdrop-blur-sm transition-colors">
                            <ChevronLeft size={32} />
                        </button>
                        <button onClick={handleNextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 p-3 rounded-full text-white backdrop-blur-sm transition-colors">
                            <ChevronRight size={32} />
                        </button>
                    </>
                )}
            </div>

            <div className="absolute bottom-6 flex gap-3 overflow-x-auto max-w-full px-4" onClick={(e) => e.stopPropagation()}>
                {safeImages.map((img, idx) => (
                    <button 
                        key={idx}
                        onClick={() => setMainImage(img)}
                        className={`w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${mainImage === img ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
                    >
                        <img src={img} className="w-full h-full object-cover" />
                    </button>
                ))}
            </div>
        </div>
      )}

      <div className="bg-white rounded-2xl max-w-4xl w-full h-auto max-h-[90vh] overflow-y-auto flex flex-col md:flex-row relative">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow hover:bg-gray-100">
            <X size={24} />
        </button>

        {/* Images Section */}
        <div className="w-full md:w-1/2 p-4 flex flex-col gap-4">
            <div className="aspect-square w-full rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center relative group cursor-zoom-in" onClick={() => setIsZoomOpen(true)}>
                <img 
                    src={mainImage} 
                    alt={product.name} 
                    className="w-full h-full object-cover" 
                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/400x400?text=Error'; }}
                />
                <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="bg-white/90 p-3 rounded-full shadow-lg">
                        <Maximize2 size={24} className="text-gray-800" />
                    </div>
                </div>
            </div>
            {safeImages.length > 1 && (
                <div className="flex gap-2 overflow-x-auto">
                    {safeImages.map((img, idx) => img && (
                        <button 
                            key={idx} 
                            onClick={() => setMainImage(img)}
                            className={`w-20 h-20 rounded-lg overflow-hidden border-2 flex-shrink-0 ${mainImage === img ? 'border-blue-600' : 'border-transparent'}`}
                        >
                            <img src={img} className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
        </div>

        {/* Details Section */}
        <div className="w-full md:w-1/2 p-8 flex flex-col">
            <div className="flex justify-between items-start">
                <span className="text-blue-600 font-bold uppercase text-sm tracking-wider">{product.category}</span>
                {averageRating && (
                    <div className="flex items-center gap-1 text-yellow-500 font-bold">
                        <Star size={16} fill="currentColor" />
                        <span>{averageRating}</span>
                        <span className="text-gray-400 font-normal text-xs">({productReviews.length} reviews)</span>
                    </div>
                )}
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mt-2">{product.name}</h1>
            
            <div className="flex items-center gap-4 mt-4">
                <span className="text-2xl font-bold text-gray-900">Rs. {(product.discountPrice || product.price || 0).toLocaleString()}</span>
                {product.discountPrice && (
                    <span className="text-lg text-gray-400 line-through">Rs. {(product.price || 0).toLocaleString()}</span>
                )}
            </div>

            <p className="text-gray-600 mt-4 leading-relaxed">{product.description}</p>

            {/* Selectors */}
            <div className="mt-8 space-y-6">
                {safeSizes.length > 0 && (
                    <div>
                        <span className="font-semibold text-gray-900 block mb-2">Select Size</span>
                        <div className="flex flex-wrap gap-2">
                            {safeSizes.map(size => (
                                <button
                                    key={size}
                                    onClick={() => setSelectedSize(size)}
                                    className={`w-12 h-12 rounded-lg border font-medium transition-all ${
                                        selectedSize === size 
                                        ? 'border-blue-600 bg-blue-50 text-blue-600' 
                                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                    }`}
                                >
                                    {size}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                 {safeColors.length > 0 && (
                    <div>
                        <span className="font-semibold text-gray-900 block mb-2">Select Color</span>
                        <div className="flex flex-wrap gap-2">
                            {safeColors.map(color => (
                                <button
                                    key={color}
                                    onClick={() => setSelectedColor(color)}
                                    className={`px-4 py-2 rounded-lg border font-medium transition-all ${
                                        selectedColor === color 
                                        ? 'border-blue-600 bg-blue-50 text-blue-600' 
                                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                    }`}
                                >
                                    {color}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Quantity Selector */}
                <div>
                     <span className="font-semibold text-gray-900 block mb-2">Quantity</span>
                     <div className="inline-flex items-center border border-gray-300 rounded-lg">
                        <button 
                            onClick={() => setQuantity(Math.max(1, quantity - 1))} 
                            className="p-3 hover:bg-gray-100 text-gray-600 border-r border-gray-300"
                            disabled={quantity <= 1}
                        >
                            <Minus size={18} />
                        </button>
                        <span className="px-6 py-2 font-bold text-gray-900 min-w-[3rem] text-center">{quantity}</span>
                        <button 
                            onClick={() => setQuantity(quantity + 1)} 
                            className="p-3 hover:bg-gray-100 text-gray-600 border-l border-gray-300"
                        >
                            <Plus size={18} />
                        </button>
                     </div>
                </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex flex-col gap-3">
                <div className="flex gap-3">
                    <button 
                        onClick={() => onAddToCart(product, selectedSize, selectedColor, quantity)}
                        className="flex-1 py-4 border-2 border-gray-900 text-gray-900 rounded-xl font-bold hover:bg-gray-50 flex items-center justify-center gap-2"
                    >
                        <ShoppingCart size={20} /> Add to Cart
                    </button>
                    <button 
                        onClick={() => onBuyNow(product, selectedSize, selectedColor, quantity)}
                        className="flex-1 py-4 bg-gray-900 text-white rounded-xl font-bold hover:bg-gray-800 flex items-center justify-center gap-2"
                    >
                        <ShoppingBag size={20} /> Order Now
                    </button>
                </div>
                <button 
                    onClick={handleWhatsAppOrder}
                    className="w-full py-4 bg-[#25D366] text-white rounded-xl font-bold hover:bg-[#128C7E] flex items-center justify-center gap-2 transition-colors"
                >
                    <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
                    Order via WhatsApp
                </button>
            </div>

            {/* Reviews Section */}
            <div className="mt-12 border-t pt-8">
                <h3 className="text-xl font-bold mb-6">Customer Reviews</h3>
                
                {/* Review Form - Only if purchased and delivered */}
                {hasPurchasedAndReceived ? (
                    !isReviewSubmitted ? (
                        <form onSubmit={handleSubmitReview} className="bg-gray-50 p-4 rounded-xl mb-8 border border-green-200">
                            <h4 className="font-bold text-sm mb-3 flex items-center gap-2 text-green-700">
                                <CheckCircle size={16} /> Verified Purchase: Write a Review
                            </h4>
                            <div className="space-y-3">
                                 <div className="flex items-center gap-2">
                                    <span className="text-sm">Rating:</span>
                                    <div className="flex">
                                        {[1, 2, 3, 4, 5].map(star => (
                                            <button 
                                                key={star} 
                                                type="button" 
                                                onClick={() => setRating(star)}
                                                className="p-1 focus:outline-none"
                                            >
                                                <Star size={20} fill={star <= rating ? "#FBBF24" : "none"} stroke={star <= rating ? "#FBBF24" : "#9CA3AF"} />
                                            </button>
                                        ))}
                                    </div>
                                 </div>
                                 
                                 {userProfile ? (
                                     <div className="w-full border rounded p-2 text-sm bg-gray-100 text-gray-700 font-medium">
                                         Reviewing as: <span className="text-blue-600 font-bold">{userProfile.username}</span>
                                     </div>
                                 ) : (
                                     <input 
                                        type="text" 
                                        placeholder="Your Name" 
                                        className="w-full border rounded p-2 text-sm"
                                        value={reviewerName}
                                        onChange={(e) => setReviewerName(e.target.value)}
                                        required
                                     />
                                 )}
                                 
                                 <textarea 
                                    placeholder="Your Comment" 
                                    className="w-full border rounded p-2 text-sm"
                                    rows={2}
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    required
                                 />
                                 <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold">Submit Review</button>
                            </div>
                        </form>
                    ) : (
                        <div className="bg-green-50 text-green-700 p-4 rounded-xl mb-8 text-sm text-center">
                            Thank you for your review!
                        </div>
                    )
                ) : (
                    <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl mb-8 text-sm text-center text-gray-500 italic">
                        You can review this item once you have purchased and received it.
                    </div>
                )}

                {/* Reviews List */}
                <div className="space-y-4">
                    {productReviews.length === 0 ? (
                        <p className="text-gray-500 text-sm">No reviews yet. Be the first to review!</p>
                    ) : (
                        productReviews.map(review => (
                            <div key={review.id} className="border-b pb-4 last:border-0">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                        <div className="bg-gray-200 p-1 rounded-full">
                                            <User size={12} />
                                        </div>
                                        <span className="font-bold text-sm">{review.customerName}</span>
                                    </div>
                                    <div className="flex text-yellow-400">
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} size={12} fill={i < review.rating ? "currentColor" : "none"} stroke="currentColor" />
                                        ))}
                                    </div>
                                </div>
                                <p className="text-gray-600 text-sm">{review.comment}</p>
                                <span className="text-xs text-gray-400 block mt-1">{new Date(review.date).toLocaleDateString()}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};